import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'

import { BELL_INACTIVITY_THRESHOLD_MS } from '@/seller.application.polling.time.config'

const DEFAULT_INACTIVITY_THRESHOLD_MS: number = BELL_INACTIVITY_THRESHOLD_MS

export type UserActivityContextValue = {
  isSoundDisabledDueToInactivity: boolean
}

const UserActivityContext = createContext<UserActivityContextValue | undefined>(
  undefined
)

const ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart'] as const

export type UserActivityProviderProps = PropsWithChildren<{
  inactivityThresholdMs?: number
}>

export const UserActivityProvider = ({
  children,
  inactivityThresholdMs = DEFAULT_INACTIVITY_THRESHOLD_MS,
}: UserActivityProviderProps) => {
  const [isSoundDisabledDueToInactivity, setIsSoundDisabledDueToInactivity] =
    useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastActivityAtRef = useRef<number>(Date.now())

  const scheduleInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    timeoutRef.current = setTimeout(() => {
      setIsSoundDisabledDueToInactivity(true)
      timeoutRef.current = null
    }, inactivityThresholdMs)
  }, [inactivityThresholdMs])

  useEffect(() => {
    const handleActivity = () => {
      lastActivityAtRef.current = Date.now()
      setIsSoundDisabledDueToInactivity(false)
      scheduleInactivityTimeout()
    }

    scheduleInactivityTimeout()

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity)
    })

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [scheduleInactivityTimeout])

  const value = useMemo<UserActivityContextValue>(
    () => ({
      isSoundDisabledDueToInactivity,
    }),
    [isSoundDisabledDueToInactivity]
  )

  return (
    <UserActivityContext.Provider value={value}>
      {children}
    </UserActivityContext.Provider>
  )
}

export const useUserActivity = (): UserActivityContextValue => {
  const context = useContext(UserActivityContext)
  if (context === undefined) {
    throw new Error(
      'useUserActivity must be used within a UserActivityProvider'
    )
  }
  return context
}
